import { SETTINGS_NAV_GROUPS } from './settingsNavGroups';
import {
  CONFIGURE_GROUPS,
  visibleConfigureSections,
} from 'dashboard/helper/kanbanConfigureSections';

const CENTER_PREFIX = 'SETTINGS_CENTER';

const descriptions = {
  ai_settings: 'AI_SETTINGS_DESCRIPTION',
  inboxes: 'CHANNELS_INBOXES_DESCRIPTION',
  voip_calls: 'CHANNELS_VOIP_CALLS_DESCRIPTION',
  agents: 'PEOPLE_AGENTS_DESCRIPTION',
  teams: 'PEOPLE_TEAMS_DESCRIPTION',
  custom_roles: 'PEOPLE_CUSTOM_ROLES_DESCRIPTION',
  assignment: 'PEOPLE_ASSIGNMENT_DESCRIPTION',
  canned: 'SUPPORT_CANNED_DESCRIPTION',
  labels: 'SUPPORT_LABELS_DESCRIPTION',
  macros: 'SUPPORT_MACROS_DESCRIPTION',
  automation: 'SUPPORT_AUTOMATION_DESCRIPTION',
  workflows: 'SUPPORT_WORKFLOWS_DESCRIPTION',
  conversation_resolution: 'SUPPORT_CONVERSATION_RESOLUTION_DESCRIPTION',
  sla: 'SUPPORT_SLA_DESCRIPTION',
  captain: 'AI_CAPTAIN_DESCRIPTION',
  agent_bots: 'AI_AGENT_BOTS_DESCRIPTION',
  lead_scoring: 'AI_LEAD_SCORING_DESCRIPTION',
  general: 'ACCOUNT_GENERAL_DESCRIPTION',
  attributes: 'ACCOUNT_ATTRIBUTES_DESCRIPTION',
  billing: 'ACCOUNT_BILLING_DESCRIPTION',
  audit: 'ACCOUNT_AUDIT_DESCRIPTION',
  integrations: 'INTEGRATIONS_DESCRIPTION',
  profile: 'PROFILE_DESCRIPTION',
  security: 'ACCOUNT_SECURITY_DESCRIPTION',
};

const terms = {
  // Nomes antigos (social selling, oportunidades) seguem nos termos para
  // quem ainda procura por eles achar os Ajustes da IA.
  ai_settings:
    'ia inteligência artificial ajustes central comentários retomadas copilot kanban conversões anúncios imóveis ligações modelos agentes social selling oportunidades',
  inboxes: 'whatsapp instagram facebook email canal caixa inbox conexão',
  voip_calls: 'telefone ligação chamada voip finalização encerramento motivo',
  assignment: 'distribuição fila responsável atribuição equipe',
  automation: 'automação regra gatilho ação',
  conversation_resolution:
    'resolver resolução automática encerrar fechar inativa parada atributos obrigatórios',
  captain: 'ia inteligência artificial assistente conhecimento',
  general: 'conta workspace nome horário geral',
  // Senha, sessões e dois fatores moram no Perfil: sem estes termos a busca
  // por eles achava só a tela de SSO (ou nada, sem SAML).
  profile:
    'preferência pessoal perfil notificações aparência senha sessões sessão dispositivos autenticação dois fatores 2fa token',
  security: 'segurança sso saml login único autenticação',
  integrations: 'integração webhook api conexão aplicativos',
};

const kanbanRoutes = {
  stages: { view: 'configure', section: 'stages' },
  automations: { view: 'configure', section: 'automations' },
  agenda: { view: 'configure', section: 'agenda' },
  ai: { view: 'configure', section: 'ai' },
  access: { view: 'configure', section: 'access' },
  appearance: { view: 'configure', section: 'appearance' },
  offers: { view: 'configure', section: 'offers' },
  integrations: { view: 'configure', section: 'integrations' },
  data: { view: 'configure', section: 'data' },
};

const kanbanItemKeys = {
  stages: 'FUNNEL_STAGES',
  automations: 'FUNNEL_AUTOMATIONS',
  agenda: 'FUNNEL_AGENDA',
  ai: 'FUNNEL_AI',
  access: 'FUNNEL_ACCESS',
  appearance: 'ACCOUNT_APPEARANCE',
  offers: 'ACCOUNT_OFFERS',
  integrations: 'ACCOUNT_INTEGRATIONS',
  data: 'ACCOUNT_DATA',
};

const toKey = value => `${CENTER_PREFIX}.${value}`;

const accountItems = SETTINGS_NAV_GROUPS.flatMap(group =>
  group.items.map(item => ({
    ...item,
    group: group.id,
    descriptionKey: toKey(
      `DESCRIPTIONS.${descriptions[item.id] || 'DEFAULT_DESCRIPTION'}`
    ),
    terms: terms[item.id] || '',
    scope: item.id === 'profile' ? 'personal' : 'account',
  }))
);

const getKanbanItems = ({ isAdmin = false, isStacklab = false } = {}) =>
  CONFIGURE_GROUPS.flatMap(group => {
    const sections = visibleConfigureSections({ isAdmin, isStacklab }).filter(
      section => section.group === group.id
    );

    return sections.map(section => ({
      id: `kanban_${section.id}`,
      labelKey: `${CENTER_PREFIX}.ITEMS.${kanbanItemKeys[section.id]}`,
      descriptionKey: `${CENTER_PREFIX}.DESCRIPTIONS.${kanbanItemKeys[section.id]}`,
      icon: section.icon,
      group: `kanban_${group.id}`,
      routeName: 'kanban_board',
      query: kanbanRoutes[section.id],
      scope: group.id === 'funnel' ? 'funnel' : 'account',
      terms: `${section.id} funil kanban configuração`,
      kanbanSection: section.id,
      requiresKanbanPermission: true,
    }));
  });

const taskItems = [
  {
    id: 'task_labels',
    labelKey: `${CENTER_PREFIX}.ITEMS.TASK_LABELS`,
    descriptionKey: `${CENTER_PREFIX}.DESCRIPTIONS.TASK_LABELS`,
    icon: 'i-ri-price-tag-3-line',
    group: 'tasks',
    routeName: 'tasks',
    query: { config_area: 'tasks', task_settings: 'labels' },
    scope: 'account',
    terms: 'tarefas etiquetas labels tags',
    permissions: ['administrator', 'agent', 'tasks_view'],
  },
  {
    id: 'task_templates',
    labelKey: `${CENTER_PREFIX}.ITEMS.TASK_TEMPLATES`,
    descriptionKey: `${CENTER_PREFIX}.DESCRIPTIONS.TASK_TEMPLATES`,
    icon: 'i-ri-file-copy-line',
    group: 'tasks',
    routeName: 'tasks',
    query: { config_area: 'tasks', task_settings: 'templates' },
    scope: 'account',
    terms: 'tarefas modelos templates padrões',
    permissions: ['administrator', 'agent', 'tasks_view'],
  },
  {
    id: 'task_views',
    labelKey: `${CENTER_PREFIX}.ITEMS.TASK_VIEWS`,
    descriptionKey: `${CENTER_PREFIX}.DESCRIPTIONS.TASK_VIEWS`,
    icon: 'i-ri-list-check-2',
    group: 'tasks',
    routeName: 'tasks',
    query: { config_area: 'tasks', task_settings: 'views' },
    scope: 'personal',
    terms: 'tarefas listas filtros visualização vistas',
    permissions: ['administrator', 'agent', 'tasks_view'],
  },
];

export const SETTINGS_CENTER_GROUPS = [
  ...SETTINGS_NAV_GROUPS.map(({ id, labelKey }) => ({ id, labelKey })),
  ...CONFIGURE_GROUPS.map(group => ({
    id: `kanban_${group.id}`,
    labelKey: `${CENTER_PREFIX}.GROUPS.${group.id.toUpperCase()}`,
  })),
  { id: 'tasks', labelKey: `${CENTER_PREFIX}.GROUPS.TASKS` },
];

export const SETTINGS_CENTER_ITEMS = [...accountItems, ...taskItems];

export const getSettingsCenterCatalog = ({
  isAdmin = false,
  isStacklab = false,
  isFeatureEnabled = () => true,
  translate = key => key,
  checkPermissions = () => true,
  checkInstallationType = () => true,
  query = '',
  area = '',
  router,
  params = {},
} = {}) => {
  const areaGroups = {
    conversations: ['channels', 'people', 'support', 'ai'],
    kanban: ['kanban_funnel', 'kanban_account'],
    agenda: ['kanban_funnel'],
    tasks: ['tasks'],
  };
  const allowedGroups = areaGroups[area] || (area ? [area] : null);
  const normalizedQuery = query
    .trim()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLocaleLowerCase();

  const visible = [
    ...SETTINGS_CENTER_ITEMS,
    ...getKanbanItems({ isAdmin, isStacklab }),
  ].filter(item => {
    if (item.adminOnly && !isAdmin) return false;
    if (item.featureFlag && !isFeatureEnabled(item.featureFlag)) return false;
    if (item.permissions && !checkPermissions(item.permissions)) return false;
    if (
      item.installationTypes &&
      !checkInstallationType(item.installationTypes)
    ) {
      return false;
    }

    if (
      item.requiresKanbanPermission &&
      !checkPermissions([
        'administrator',
        'agent',
        'kanban_manage',
        'kanban_view',
      ])
    ) {
      return false;
    }

    if (allowedGroups && !allowedGroups.includes(item.group)) {
      return false;
    }

    if (!normalizedQuery) return true;
    const text = [
      translate(item.labelKey),
      translate(item.descriptionKey),
      item.terms,
      item.group,
    ]
      .join(' ')
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .toLocaleLowerCase();
    return text.includes(normalizedQuery);
  });

  return visible.filter(item => {
    if (!router || !item.routeName) return true;
    try {
      const resolved = router.resolve({
        name: item.routeName,
        params,
        query: item.query,
      });
      if (
        resolved.meta?.permissions &&
        !checkPermissions(resolved.meta.permissions)
      ) {
        return false;
      }
      if (
        resolved.meta?.featureFlag &&
        !isFeatureEnabled(resolved.meta.featureFlag)
      ) {
        return false;
      }
      // Cobranca so existe no cloud: fora dele a tela volta para as conversas.
      if (
        resolved.meta?.installationTypes &&
        !checkInstallationType(resolved.meta.installationTypes)
      ) {
        return false;
      }
      return true;
    } catch {
      return false;
    }
  });
};

// O menu lateral usa o mesmo filtro da Central (busca sem acento, que olha
// descricao e termos, e a mesma checagem de rota): antes ele so comparava o
// nome e exigia acento, e 'distribuicao' achava na Central e nao no menu.
// A diferenca que fica e o fail-open do menu: no boot o getter de feature pode
// ainda nao ter respondido, e sumir com o item nesse instante e pior do que
// mostrar um a mais.
const NAV_GROUP_IDS = SETTINGS_NAV_GROUPS.map(group => group.id);

export const getSettingsNavGroups = ({
  isFeatureEnabled = () => true,
  ...options
} = {}) => {
  const items = getSettingsCenterCatalog({
    ...options,
    isFeatureEnabled: flag => isFeatureEnabled(flag) !== false,
    area: '',
  }).filter(item => NAV_GROUP_IDS.includes(item.group));

  return SETTINGS_NAV_GROUPS.map(group => ({
    ...group,
    items: items.filter(item => item.group === group.id),
  })).filter(group => group.items.length > 0);
};

export const getSettingsCenterGroups = ({
  items = SETTINGS_CENTER_ITEMS,
  translate = key => key,
} = {}) =>
  SETTINGS_CENTER_GROUPS.map(group => ({
    ...group,
    label: translate(group.labelKey),
    items: items.filter(item => item.group === group.id),
  })).filter(group => group.items.length);

export const settingsCenterKey = value => toKey(value);
